import axios, { AxiosError, CancelToken } from 'axios';
import { useMemo } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { Primitive } from 'type-fest';

type ParamValue = Primitive;
export type Params = {
  url: null | string;
  params?: { [k: string]: ParamValue | ParamValue[] };
  unpackData?: boolean;
  swrOptions?: SWRConfiguration;
  handlerDisabled?: boolean;
};

export type SearchQueryStatus = {
  isLoading: boolean;
  /** @deprecated use isLoading */
  loading: boolean;
  error?: AxiosError | null;
  success: boolean;
  isValidating: boolean;
};

type FetcherParams = {
  cancelToken?: CancelToken;
  handlerDisabled?: boolean;
  unpackData?: boolean;
  cachedValue?: any;
};
const fetcher = (url: string, { cachedValue, unpackData = false, ...rest }: FetcherParams) =>
  axios.get(url.replace('#unpacked', ''), rest).then(
    ({ data }) => (unpackData ? data.data : data),
    (ex) => {
      if (!axios.isCancel(ex)) {
        throw ex;
      }

      return cachedValue;
    },
  );

export const stringifyParams = (params: Params['params']) => {
  if (params) {
    const processedParams = Object.fromEntries(
      Object.entries(params)
        .filter(([, v]) => !!v && v.toString() !== '')
        .map(([k, v]) => [k, String(v)]),
    );
    return new URLSearchParams(processedParams).toString();
  }

  return null;
};

const useHttpQuery = <T>({ url: urlToFetch, params, swrOptions, unpackData, ...rest }: Params) => {
  const { cache } = useSWRConfig();
  const key = useMemo(() => {
    if (!urlToFetch) {
      return null;
    }
    const stringifiedParams = stringifyParams(params);
    if (stringifiedParams) {
      const joiner = urlToFetch.includes('?') ? '&' : '?';
      return `${urlToFetch}${joiner}${stringifiedParams}${unpackData ? '#unpacked' : ''}`;
    }
    return urlToFetch;
  }, [params, unpackData, urlToFetch]);
  const cachedValue = key ? cache.get(key)?.data : undefined;
  const { data, error, mutate, isValidating, isLoading } = useSWR<T, AxiosError<any>>(
    key,
    (url) => fetcher(url, { cachedValue, unpackData, ...rest }),
    swrOptions,
  );

  const success = !error && !!data;

  return [data, { loading: isLoading, isLoading, success, error, isValidating }, mutate] as const;
};

export default useHttpQuery;
